import React, {useLayoutEffect} from "react";
import {useSelector} from "react-redux";
import users from "../../users";
import {FullWindowSpinner} from "../../shared";
import {cn} from "../../../utils/utils";

import './hero-layout.scss';
import {Link, useLocation} from "react-router-dom";

export type HeroLayoutRoute = {
    icon: React.ReactNode;
    text: string;
    route: string;
    hidden?: boolean;
}

export type HeroLayoutProps = {
    children: React.ReactNode;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    options?: React.ReactNode;
    routes?: HeroLayoutRoute[];
    imgUrl?: string;
    backgroundUrl?: string;
    loading?: boolean;
    imgStyleOverrides?: React.CSSProperties;
    maxHeaderHeight?: number;
    minHeaderHeight?: number;
    customBackgroundPosition?: string;
    topRightElement?: React.ReactNode;
}

const HeroLayout = (props: HeroLayoutProps) => {

    const {
        children,
        title,
        subtitle,
        imgUrl,
        options,
        routes,
        backgroundUrl,
        loading,
        topRightElement,
    } = props;

    const location = useLocation();
    const darkMode = useSelector(users.selectors.isDarkMode);
    const headerRef = React.useRef(null);
    const parentRef = React.useRef(null);
    const [maxHeaderHeight, setMaxHeaderHeight] = React.useState(369);
    const [currentHeaderHeight, setCurrentHeaderHeight] = React.useState(369);


    useLayoutEffect(() => {
        if (!parentRef.current) {
            parentRef.current = document.getElementById('home__scrollContainer');
        }
        if (!headerRef.current || !parentRef.current) {
            return;
        }

        headerRef.current.style.width = `${parentRef.current.offsetWidth}px`;
        const imgRef = headerRef.current.querySelector('.hero-layout__user-card__main-img');
        const displayNameRef = headerRef.current.querySelector('.hero-layout__user-card__display-name');
        const emailRef = headerRef.current.querySelector('.hero-layout__user-card__email');

        const handleScroll = () => {
            headerRef.current.style.width = `${parentRef.current.offsetWidth}px`;
            const scrollTop = parentRef.current.scrollTop;
            const maxHeight = props.maxHeaderHeight ?? Math.min(369, parentRef.current.offsetWidth / 1.5);
            setMaxHeaderHeight(maxHeight);
            const absMinHeight = props.minHeaderHeight ?? 120;
            const minHeight = Math.min(absMinHeight, parentRef.current.offsetWidth / 2);
            const height = Math.max(120, minHeight, maxHeight - scrollTop);
            headerRef.current.style.height = `${height}px`;
            setCurrentHeaderHeight(height);
            const breakpoints = [
                {
                    threshold: 999,
                    styles: [{
                        ref: imgRef,
                        left: "10px",
                        height: '35%',
                        top: '10%',
                        maxWidth: '40%',
                        transform: 'scale(1)',
                    }, {
                        ref: displayNameRef,
                        top: '20px',
                        transform: 'translateY(0)',
                        left: '100px',
                    }, {
                        ref: emailRef,
                        opacity: 1,
                        top: '50px',
                        transform: 'translateY(0)',
                        left: '100px',
                    }]
                },
                {
                    threshold: 370,
                    styles: [{
                        ref: imgRef,
                        left: "10px",
                        height: '80px',
                        top: '10px',
                        maxWidth: '40%',
                    }, {
                        ref: displayNameRef,
                        top: '20px',
                        transform: 'translateY(0)',
                        left: '100px',
                    }, {
                        ref: emailRef,
                        opacity: 1,
                        top: '50px',
                        transform: 'translateY(0)',
                        left: '100px',
                    }]
                },
                {
                    threshold: 150,
                    styles: [{
                        ref: imgRef,
                        left: "5px",
                        height: 'calc(100% - 70px)',
                        top: '5px',
                    }, {
                        ref: displayNameRef,
                        top: 'calc(50% - 45px)',
                        apply: () => {
                            displayNameRef.style.left = `${imgRef.offsetWidth + 15}px`;
                        }
                    }, {
                        ref: emailRef,
                        opacity: 0,
                    }]
                }
            ]

            if (imgRef) {
                imgRef.style = {};
                props.imgStyleOverrides && Object.keys(props.imgStyleOverrides).forEach((key) => {
                    imgRef.style[key] = props.imgStyleOverrides[key];
                });
            }
            displayNameRef.style = {};
            emailRef.style = {};

            breakpoints.forEach((bp) => {
                if (height < bp.threshold) {
                    bp.styles.forEach((style) => {
                        Object.keys(style).forEach((key) => {
                            if (!style.ref) return;
                            style.ref.style[key] = style[key];
                            if (style.apply) {
                                style.apply();
                            }
                        });
                    });
                }
            });
        };

        handleScroll();

        const resizeObserver = new ResizeObserver(() => {
            handleScroll();
        });
        resizeObserver.observe(parentRef.current);

        parentRef.current.addEventListener('scroll', handleScroll);

        return () => {
            if (parentRef.current) {
                parentRef.current.removeEventListener('scroll', handleScroll);
                resizeObserver.unobserve(parentRef.current);
            }
        };
    }, [props.imgStyleOverrides, props.maxHeaderHeight, props.minHeaderHeight]);

    if (loading) {
        return <FullWindowSpinner/>
    }

    return (<div className={cn({"hero-layout-dark": darkMode})}>
        <div
            ref={headerRef}

            style={{
                background: backgroundUrl ? `url(${backgroundUrl}) ${props.customBackgroundPosition ?? "center/cover no-repeat"}` : (darkMode ? 'rgb(33, 37, 41)' : 'white'),
                border: backgroundUrl ? 'none' : undefined,
            }}
            className="hero-layout__user-card">
            <div className="hero-layout__user-card__backdrop" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
            }}></div>
            {topRightElement && (
                <div className="hero-layout__top-right" style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 10,
                }}>
                    {topRightElement}
                </div>
            )}
            {imgUrl && <img className="hero-layout__user-card__main-img" src={imgUrl}
                            alt="user_photo"/>}
            <h3 className="m-0 hero-layout__user-card__display-name">{title}</h3>
            <p className="hero-layout__user-card__email">{subtitle}</p>
            <div className="hero-layout__subroutes">
                {!!routes?.length &&
                    routes.map((route) => (
                        <Link hidden={route.hidden} to={route.route} key={route.route}
                              className={cn("hero-layout__subroutes-btn", {
                                  "active": location.pathname.includes(route.route)
                              })}>
                            {route.icon}
                        </Link>
                    ))
                }
                {options}
            </div>
        </div>
        <div className="d-flex flex-column gap-5 mb-5" style={{
            marginTop: maxHeaderHeight,
        }}>
            {children}
        </div>
    </div>);
}

export default HeroLayout;
